import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { GalleryDataAccess, type UnifiedGalleryImage } from '@/features/gallery/api/galleryDataAccess'
import type { CaptionAgentContext } from '@/features/social/types/captionAgent'

type GalleryContextQuery = {
  context?: CaptionAgentContext
}

export interface GalleryPostContextSummary {
  galleryId: number
  title?: string | null
  franchiseRegion?: string | null
  jobNumber?: number | string | null
  location?: string | null
  tags: string[]
  media: Array<{
    id: number
    url: string
    type: 'job_image' | 'gallery_photo'
    phase?: string | null
    caption?: string | null
  }>
  details?: {
    serviceCity?: string | null
    serviceProvince?: string | null
    serviceDate?: string | null
    clientName?: string | null
    notes?: string | null
  }
}

function extractPhase(image: UnifiedGalleryImage): string | null {
  if ('phase' in image) {
    const candidate = (image as UnifiedGalleryImage & { phase?: string | null }).phase
    return candidate ?? null
  }
  return null
}

async function fetchGalleryContext(context: CaptionAgentContext): Promise<GalleryPostContextSummary> {
  if (context.source !== 'gallery') {
    throw new Error('Unsupported context source')
  }

  const { data, error } = await supabase
    .from('galleries')
    .select(`
      id,
      title,
      franchise_region,
      job_number,
      gallery_jobber_details (
        job_id,
        client_name,
        service_city,
        service_province,
        service_postal_code,
        service_address,
        service_date,
        notes
      ),
      gallery_tag_relations (
        gallery_tags (
          id,
          name,
          slug
        )
      )
    `)
    .eq('id', context.galleryId)
    .single()

  if (error) {
    throw error
  }

  const images = await GalleryDataAccess.getGalleryImages(context.galleryId)
  const approved = images.filter(img => img.review_status === 'approved')

  const jobberDetails = Array.isArray((data as any)?.gallery_jobber_details)
    ? (data as any).gallery_jobber_details[0]
    : (data as any)?.gallery_jobber_details

  const locationParts = [
    jobberDetails?.service_city,
    jobberDetails?.service_province
  ].filter(Boolean)

  const tagRelations = (data?.gallery_tag_relations ?? []) as Array<{
    gallery_tags?: { name?: string | null } | null
  }>

  return {
    galleryId: data?.id ?? context.galleryId,
    title: data?.title,
    franchiseRegion: data?.franchise_region,
    jobNumber: data?.job_number || context.jobNumber || null,
    location: locationParts.length ? locationParts.join(', ') : null,
    tags: tagRelations
      .map(relation => relation?.gallery_tags?.name)
      .filter((name): name is string => typeof name === 'string' && name.length > 0),
    media: approved.map(img => ({
      id: img.original_id,
      url: img.image_url,
      type: img.image_type,
      phase: extractPhase(img),
      caption: img.caption
    })),
    details: jobberDetails
      ? {
          serviceCity: jobberDetails.service_city,
          serviceProvince: jobberDetails.service_province,
          serviceDate: jobberDetails.service_date,
          clientName: jobberDetails.client_name,
          notes: jobberDetails.notes
        }
      : undefined
  }
}

export function useGalleryPostContext({ context }: GalleryContextQuery) {
  return useQuery({
    queryKey: ['gallery-post-context', context?.source, context?.galleryId],
    queryFn: async () => {
      if (!context) return null
      return fetchGalleryContext(context)
    },
    enabled: Boolean(context),
    staleTime: 1000 * 60 * 5
  })
}
